import React, { useEffect } from 'react'
import { Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error'

export interface ToastProps {
  variant: ToastVariant
  message: string
  duration?: number
  onDismiss?: () => void
}

const variantConfig: Record<ToastVariant, { icon: React.ReactNode; className: string }> = {
  info: {
    icon: <Info size={18} />,
    className: 'bg-ink text-parchment',
  },
  success: {
    icon: <CheckCircle size={18} />,
    className: 'bg-green text-parchment',
  },
  warning: {
    icon: <AlertTriangle size={18} />,
    className: 'bg-terra text-white',
  },
  error: {
    icon: <XCircle size={18} />,
    className: 'bg-red-700 text-white',
  },
}

const Toast: React.FC<ToastProps> = ({ variant, message, duration = 4000, onDismiss }) => {
  const { t } = useTranslation()
  useEffect(() => {
    if (duration <= 0) return
    const timer = setTimeout(() => {
      onDismiss?.()
    }, duration)
    return () => clearTimeout(timer)
  }, [duration, onDismiss])

  const config = variantConfig[variant]

  return (
    <div
      className={[
        'flex items-center gap-3 px-5 py-3.5 rounded-warmer shadow-lg font-body text-sm',
        'max-w-sm w-[calc(100%-2rem)] transition-all duration-300',
        'animate-in slide-in-from-bottom-4 fade-in duration-300',
        config.className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="alert"
    >
      <span className="shrink-0 opacity-80">{config.icon}</span>
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          aria-label={t('common.dismiss')}
        >
          <XCircle size={16} />
        </button>
      )}
    </div>
  )
}

export default Toast
